import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { isEqualWith } from 'lodash';

import { useDebouncedSave } from 'hooks/useDebounceSave';

import { AlertComponent } from 'components/_basic/AlertComponent';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { IPeriodsTableRows } from 'components/BusinessToolForm/constants/periods';
import {
  GLOBAL_PERIOD_NAME,
  GLOBAL_PERIOD_TABLE_HEADINGS_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';
import {
  convertToOpposite,
  hasOnlyZeroPeriodValues,
  PERIODS_OPERATION_TYPES,
  summarizeSubtractPeriodValues,
} from 'components/BusinessToolForm/utils/helpers';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { FundingDirectionsFormTable } from './components/FundingDirectionsFormTable';
import { IFundingDirectionsFormProps } from './types';
import { errorMessages, FUNDING_DIRECTIONS_NAMES, IFundingDirectionsErrorCallback } from './utils/form';
import { FullScreenWrapper } from '../../../components/FullScreenWrapper';
import { useUnitChange } from '../../../hooks/useUnitChange';
import { INVESTING_ACTIVITIES_NAMES } from '../../../ProjectedCashFlow/DirectForm/DirectFormComponents/Sections/InvestingActivities/utils/form';
import { investingActivitiesPrefix } from '../../../ProjectedCashFlow/utils/form';
import {
  DEFAULT_DEBOUNCE_TIME_CASH_FLOW_ELEMENTS,
  FIRST_COLUMN_WIDTH,
  fundingDirectionsPrefix,
} from '../../utils/form';
import { PeriodFormHeader } from '../components/PeriodFormHeader';

import styles from './FundingDirectionsForm.module.scss';

export const FundingDirectionsForm: FC<IFundingDirectionsFormProps> = ({ disabled }) => {
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const { t } = useTranslation();
  const { key } = useUnitChange();

  const { control, setValue } = useFormContext();

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const columnsNames = getPeriodColumnsNames(period);

  const tableName = concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.TABLE);
  const totalName = concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.TOTAL);

  const { fields } = useFieldArray({ control, name: tableName });
  const fundingDirectionsTable = useWatch({ name: tableName });

  const variantsName = concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.VARIANTS, ':index');

  const purchasePropertyField = concatWithDot(
    investingActivitiesPrefix,
    INVESTING_ACTIVITIES_NAMES.PURCHASE_PROPERTY_PLANT_EQUIPMENT,
  );

  const fundsHandler = useCallback((values: IPeriodsTableRows[]) => {
    const constructionEquipment = values.slice(3, 5);
    const purchaseProperty = summarizeSubtractPeriodValues(
      [constructionEquipment[0], constructionEquipment[1]],
      PERIODS_OPERATION_TYPES.ADD,
    );
    const convertedToNegativePurchaseProperty = convertToOpposite(purchaseProperty);

    setValue(purchasePropertyField, convertedToNegativePurchaseProperty);
  }, []);

  const totalFiledHandler = useCallback((values: IPeriodsTableRows[]) => {
    const totalValue = summarizeSubtractPeriodValues(values, PERIODS_OPERATION_TYPES.ADD);
    setValue(totalName, totalValue);
  }, []);

  useDebouncedSave({
    data: fundingDirectionsTable,
    saveFunction: fundsHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_CASH_FLOW_ELEMENTS,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: fundingDirectionsTable,
    saveFunction: totalFiledHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_CASH_FLOW_ELEMENTS,
    skipExecutionOnFirstRender: true,
  });

  const rAndDSubRows = useWatch({
    name: concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.R_AND_D_SUB_ROWS),
  });
  const marketingAndSalesSubRows = useWatch({
    name: concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.MARKETING_AND_SALES_SUB_ROWS),
  });
  const workingCapitalSubRow = useWatch({
    name: concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.WORKING_CAPITAL_SUB_ROW),
  });
  const constructionSubRow = useWatch({
    name: concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.CONSTRUCTION_SUB_ROW),
  });
  const equipmentPurchaseSubRow = useWatch({
    name: concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.EQUIPMENT_PURCHASE_SUB_ROW),
  });
  const otherSubRow = useWatch({
    name: concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.OTHER_SUB_ROW),
  });

  const setFundingDirectionError = useCallback(
    ({ subRowValues, fundingIndex, errorMessage }: IFundingDirectionsErrorCallback) => {
      const subRowSum = summarizeSubtractPeriodValues(subRowValues, PERIODS_OPERATION_TYPES.ADD);

      const isSubRowsSumEqualRow = isEqualWith(subRowSum, fundingDirectionsTable[fundingIndex], (val1, val2) => {
        if (typeof val1 === 'number' && typeof val2 === 'string') {
          return Number(val2) === val1 || (val1 === 0 && val2 === '');
        }
        if (typeof val1 === 'string' && typeof val2 === 'number') {
          return Number(val1) === val2 || (val2 === 0 && val1 === '');
        }
        return undefined; 
      });

      if (isSubRowsSumEqualRow || hasOnlyZeroPeriodValues(subRowValues)) {
        setErrorMessage((errorMessages) => errorMessages.filter((error) => error !== errorMessage));
      } else {
        setErrorMessage((errorMessages) => [...errorMessages, errorMessage]);
      }
    },
    [fundingDirectionsTable],
  );

  useEffect(
    () =>
      setFundingDirectionError({ subRowValues: rAndDSubRows, fundingIndex: 0, errorMessage: t(errorMessages.rAndD) }),
    [rAndDSubRows, setFundingDirectionError],
  );
  useEffect(
    () =>
      setFundingDirectionError({
        subRowValues: marketingAndSalesSubRows,
        fundingIndex: 1,
        errorMessage: t(errorMessages.marketingAndSales),
      }),
    [marketingAndSalesSubRows, setFundingDirectionError],
  );
  useEffect(
    () =>
      setFundingDirectionError({
        subRowValues: workingCapitalSubRow,
        fundingIndex: 2,
        errorMessage: t(errorMessages.workingCapital),
      }),
    [workingCapitalSubRow, setFundingDirectionError],
  );
  useEffect(
    () =>
      setFundingDirectionError({
        subRowValues: constructionSubRow,
        fundingIndex: 3,
        errorMessage: t(errorMessages.construction),
      }),
    [constructionSubRow, setFundingDirectionError],
  );
  useEffect(
    () =>
      setFundingDirectionError({
        subRowValues: equipmentPurchaseSubRow,
        fundingIndex: 4,
        errorMessage: t(errorMessages.equipmentPurchase),
      }),
    [equipmentPurchaseSubRow, setFundingDirectionError],
  );
  useEffect(
    () =>
      setFundingDirectionError({ subRowValues: otherSubRow, fundingIndex: 5, errorMessage: t(errorMessages.other) }),
    [otherSubRow, setFundingDirectionError],
  );

  return (
    <FormSectionCard
      collapsible
      title={t('financialPlan.cashFlowElements.fundingDirections.title')}
      disabled={disabled}
    >
      <FullScreenWrapper title={t('financialPlan.cashFlowElements.fundingDirections.title')} disabled={disabled}>
        <Fragment key={key}>
          <PeriodFormHeader
            disabled={disabled}
            periodId="fundingDirectionsFormHeader.period"
            currencyUnitName={concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.CURRENCY)}
          />
          <FormSectionFilledCard>
            <FundingDirectionsFormTable
              showSumRow
              name={tableName}
              totalName={totalName}
              headerName={GLOBAL_PERIOD_TABLE_HEADINGS_NAME}
              columnsNames={columnsNames || []}
              rows={fields}
              disabled={disabled}
              watchRowTitleName={variantsName}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
            />
          </FormSectionFilledCard>
        </Fragment>
      </FullScreenWrapper>
      {!!errorMessage.length && <AlertComponent message={errorMessage[0]} severity="error" className={styles.error} />}
    </FormSectionCard>
  );
};
